"use client";

import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { CountryCodeConfig, CountryCode } from '@/data/countryCodes';
import { useCountryFilter } from './hooks/useCountryFilter';
import { useCountrySelect } from './hooks/useCountrySelect';
import { cn } from '@/lib/utils';

interface CountryCodeSelectorProps {
  value: CountryCode;
  onChange: (value: CountryCode) => void;
  countryList: CountryCodeConfig[];
  className?: string;
  error?: boolean;
}

export const CountryCodeSelector: React.FC<CountryCodeSelectorProps> = ({
  value,
  onChange,
  countryList,
  className,
  error = false,
}) => {
  const { filter, setFilter, filteredList } = useCountryFilter(countryList);
  const { selectedFlag, selectedConfig } = useCountrySelect({
    value,
    onChange,
    countryList,
  });

  return (
    <div className={cn('relative', className)}>
      <Listbox value={value} onChange={onChange}>
        {({ open }) => (
          <>
            <Listbox.Button
              className={cn(
                'relative w-full cursor-pointer rounded-md border border-gray-200 bg-[#FAFAF9] h-full pl-3 pr-10 text-left',
                'focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2',
                'sm:text-sm',
                error && 'border-red-500'
              )}
            >
              <span className="flex items-center">
                {selectedFlag && (
                  <span className="mr-2 text-base leading-none">{selectedFlag}</span>
                )}
                <span className="block truncate tabular-nums text-black">
                  {selectedConfig?.code || value}
                </span>
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown
                  className={cn(
                    'h-5 w-5 text-gray-400 transition-transform duration-200',
                    open && 'rotate-180'
                  )}
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={React.Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                static
                className="absolute z-10 max-h-60 min-w-[320px] w-[320px] overflow-auto rounded-md bg-white mt-2 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              >
                {/* Search Input */}
                <div className="sticky -top-2 z-10 bg-white p-2">
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Search className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="text"
                      value={filter}
                      onChange={setFilter}
                      placeholder="Search country..."
                      className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-black placeholder:text-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {filter && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilter({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Country List */}
                {filteredList.length === 0 ? (
                  <div className="relative cursor-default select-none py-3 px-4 text-gray-700">
                    No countries found.
                  </div>
                ) : (
                  filteredList.map((country) => (
                    <Listbox.Option
                      key={country.code}
                      className={({ active, selected }) =>
                        cn(
                          'relative cursor-pointer select-none py-3 px-3',
                          active ? 'bg-gray-100' : 'text-gray-900',
                          selected && 'bg-gray-50'
                        )
                      }
                      value={country.code}
                    >
                      {({ selected }) => (
                        <div className="flex items-center gap-2">
                          <div className="flex h-4 w-4 items-center justify-center">
                            {selected ? (
                              <Check className="h-4 w-4 text-black" aria-hidden="true" />
                            ) : null}
                          </div>
                          <span className="text-base leading-none">{country.flag}</span>
                          <span className="text-right text-sm tabular-nums text-black">
                            {country.code}
                          </span>
                          <span className="truncate text-sm text-gray-700">{country.name}</span>
                        </div>
                      )}
                    </Listbox.Option>
                  ))
                )}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
};

